import { Injectable } from '@angular/core';

import '@kitware/vtk.js/Rendering/Profiles/Geometry';

import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkCubeSource from '@kitware/vtk.js/Filters/Sources/CubeSource';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';

import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

// @ts-ignore
import vtkInteractorStyleTrackballCamera from '@kitware/vtk.js/Interaction/Style/InteractorStyleTrackballCamera';

@Injectable({ providedIn: 'root' })
export class CubeService {
  private renderWindow: vtkRenderWindow;
  private renderer: vtkRenderer;

  constructor() {
    const renderWindow = vtkRenderWindow.newInstance();
    const renderer = vtkRenderer.newInstance({ background: [0.98, 0.98, 0.98] });
    renderWindow.addRenderer(renderer);

    this.renderer = renderer;
    this.renderWindow = renderWindow;

    this.renderWindow.render();
  }

  private createWireFrame(meshing: any) {
    console.log(meshing);

    const cube = meshing;
    const zones = meshing.zones as any[];

    // create outer cube
    const cubePipeline = this.createCubePipeline(cube.lx.value, cube.ly.value, cube.lz.value);
    this.renderer.addActor(cubePipeline.actor);
    const colors = [
      { r: 0, g: 0, b: 0 },
      { r: 1, g: 0, b: 0 },
      { r: 0, g: 0, b: 1 },
    ];

    let totalZ = 0;
    zones.forEach((zone: any, i: number = 0) => {
      totalZ += zone.lz.value;
      const colorIndex = zone.is_porous === true ? i : 0;
      // creates inter cubes
      const cubePipeline = this.createInnerCubePipeline(
        zone.lx.value,
        zone.ly.value,
        totalZ,
        i,
        cube,
        zone.is_porous,
        colors[colorIndex]
      );
      this.renderer.addActor(cubePipeline.actor);
    });

    // let cubeAxesActor = vtkCubeAxesActor.newInstance();

    // const cubeAxes = cubeAxesActor;
    // cubeAxes.setCamera(this.renderer.getActiveCamera());

    // cubeAxes.getProperty().setColor(0, 0, 0);

    // let bounds = cubePipeline.actor.getBounds();

    // cubeAxes.setDataBounds(bounds);
    // this.renderer.addActor(cubeAxes);

    this.renderer.resetCamera();
    this.renderWindow.render();
  }

  private createCubePipeline(x: number, y: number, z: number) {
    const cubeSource = vtkCubeSource.newInstance();
    const actor = vtkActor.newInstance();
    const mapper = vtkMapper.newInstance();

    actor.setMapper(mapper);
    mapper.setInputConnection(cubeSource.getOutputPort());

    actor.getProperty().setOpacity(0.5);
    // actor.getProperty().setColor(0, 0, 0);

    cubeSource.setXLength(x);
    cubeSource.setYLength(y);
    cubeSource.setZLength(z);

    cubeSource.setCenter(0, 0, 0);

    return { cubeSource, mapper, actor };
  }

  private createInnerCubePipeline(
    x: number,
    y: number,
    z: number,
    id = 0,
    cube: any,
    visible = true,
    colors: { r: number; g: number; b: number }
  ) {
    const cx = (cube.lx.value !== 0 ? cube.lx.value / 2 : 1e-6) - cube.lx.value / 2;
    const cy = (cube.ly.value !== 0 ? cube.ly.value / 2 : 1e-6) - cube.ly.value / 2;

    const cubeSource = vtkCubeSource.newInstance();
    const actor = vtkActor.newInstance();
    const mapper = vtkMapper.newInstance();

    actor.setMapper(mapper);
    mapper.setInputConnection(cubeSource.getOutputPort());

    if (visible) {
      actor.getProperty().setOpacity(0.8);
      actor.getProperty().setColor(colors.r, colors.g, colors.b);
    }
    if (!visible) {
      actor.getProperty().setOpacity(0);
      actor.getProperty().setColor(0, 0, 0);
    }

    cubeSource.setXLength(x);
    cubeSource.setYLength(y);
    cubeSource.setZLength(z);

    const cz = -(cube.lz.value / 2) + z / 2;
    cubeSource.setCenter(cx, cy, cz);

    return { cubeSource, mapper, actor };
  }

  public initialize(renderDiv: HTMLElement, meshing: any) {
    this.createWireFrame(meshing);

    // ----------------------------------------------------------------------------
    // Use OpenGL as the backend to view the all this
    // ----------------------------------------------------------------------------

    const openglRenderWindow = vtkOpenGLRenderWindow.newInstance();
    this.renderWindow.addView(openglRenderWindow);

    // ----------------------------------------------------------------------------
    // Set div to put this into the container
    // ----------------------------------------------------------------------------

    openglRenderWindow.setContainer(renderDiv);

    // ----------------------------------------------------------------------------
    // Capture size of the container and set it to the renderWindow
    // ----------------------------------------------------------------------------

    const { width, height } = renderDiv.getBoundingClientRect();
    openglRenderWindow.setSize(width, height);

    // ----------------------------------------------------------------------------
    // Setup an interactor to handle mouse events
    // ----------------------------------------------------------------------------

    const interactor = vtkRenderWindowInteractor.newInstance();
    interactor.setView(openglRenderWindow);
    interactor.initialize();
    interactor.bindEvents(renderDiv);

    // ----------------------------------------------------------------------------
    // Setup interactor style to use
    // ----------------------------------------------------------------------------

    interactor.setInteractorStyle(vtkInteractorStyleTrackballCamera.newInstance());

    this.renderer.resetCamera();
  }

  public updateCube(meshing: any) {
    const actors = this.renderer.getActors();

    actors.forEach((actor) => {
      this.renderer.removeActor(actor);
    });

    this.renderer.getRenderWindow()?.render();

    setTimeout(() => {
      this.createWireFrame(meshing);
      this.renderer.resetCamera();
      this.renderWindow.render();
    });
  }
}
